import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, TextField, Button, CircularProgress, MenuItem, Paper, Avatar, Chip,
  FormControl, FormControlLabel, FormLabel, InputLabel, Radio, RadioGroup, Select, Switch,
  IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, FormHelperText, Divider,
  Autocomplete
} from '@mui/material';
import { PhotoCamera, Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate, useParams, Link } from 'react-router-dom';
import userService, { User, UserUpdatePayload } from '../../services/userService'; // 正しいインポート元
import { Customer } from '../../types/customer';
import PageHeader from '../../components/layout/PageHeader';
import DashboardLayout from '../../components/layout/DashboardLayout';
import config from '../../config';
import { SelectChangeEvent } from '@mui/material';
import { BlockedCustomer } from '../../types/blockedCustomer';
import blockedCustomerService from '../../services/blockedCustomerService';
import { NgHotel } from '../../types/ngHotel';
import ngHotelService from '../../services/ngHotelService';
import castWaitingLocationService, { CastWaitingLocation } from '../../services/castWaitingLocationService';
import { systemSettingsService } from '../../services/systemSettingsService';

// FormDataは入力フィールドの文字列を保持する役割に特化
interface FormData {
  // ... (以前のFormDataと同様のフィールド)
  profile_image: File | null;
  username: string;
  password: string;
  current_password: string;
  phone_number: string;
  email: string;
  line_account: string;
  role: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  customer_name: string;
  furigana: string;
  gender: 'male' | 'female' | 'other' | 'unspecified';
  birthday: string;
  postal_code: string;
  address: string;
  contact_email: string;
  payment_method: string;
  company_name: string;
  notes: string;
  blacklisted: boolean;
  blacklist_reason: string;
  ng_cast_ids: string[];
  options: string[];
  nomination_fee: string;
  join_date: string;
  age: string;
  height: string;
  bust: string;
  cup: string;
  waist: string;
  hip: string;
  spec: string;
  special_nomination_fee: string;
  // ドライバー向け車両情報
  car_model: string;
  car_number: string;
  car_color: string;
}

interface FormErrors {
  [key: string]: string;
}

const EditUser: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Userオブジェクト全体をstateで保持する
  const [user, setUser] = useState<User | null>(null);
  
  // フォームの入力値は別途stateで管理
  const [formData, setFormData] = useState<FormData>({
    profile_image: null, username: '', password: '', current_password: '', phone_number: '',
    email: '', line_account: '', role: '', first_name: '', last_name: '', is_active: true,
    customer_name: '', furigana: '', gender: 'unspecified', birthday: '', postal_code: '',
    address: '', contact_email: '', payment_method: '', company_name: '', notes: '',
    blacklisted: false, blacklist_reason: '', ng_cast_ids: [], options: [], nomination_fee: '',
    join_date: '', age: '', height: '', bust: '', cup: '', waist: '', hip: '', spec: '',
    special_nomination_fee: '',
    car_model: '', car_number: '', car_color: '',
  });
  
  const [loading, setLoading] = useState<boolean>(true);
  const [savingLoading, setSavingLoading] = useState<boolean>(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [casts, setCasts] = useState<any[]>([]);
  const [filteredCasts, setFilteredCasts] = useState<any[]>([]);
  const [castSearchTerm, setCastSearchTerm] = useState<string>('');

  const [errors, setErrors] = useState<FormErrors>({});
  const [blockedCustomers, setBlockedCustomers] = useState<BlockedCustomer[]>([]);
  const [newBlockedPhone, setNewBlockedPhone] = useState('');
  const [newBlockedNotes, setNewBlockedNotes] = useState('');

  // NGホテル
  const [ngHotels, setNgHotels] = useState<NgHotel[]>([]);
  const [hotelOptions, setHotelOptions] = useState<any[]>([]);
  const [selectedNgHotel, setSelectedNgHotel] = useState<any | null>(null);
  const [newNgHotelNotes, setNewNgHotelNotes] = useState('');

  // キャスト個別の待機場所（複数）
  const [castWaitingLocations, setCastWaitingLocations] = useState<CastWaitingLocation[]>([]);
  const [newWaitingName, setNewWaitingName] = useState('');
  const [newWaitingKana, setNewWaitingKana] = useState('');
  const [newWaitingAddress, setNewWaitingAddress] = useState('');
  const [savingCastWaiting, setSavingCastWaiting] = useState(false);

  const fetchBlockedCustomers = useCallback(async () => {
    if (id && formData.role === 'CAST') {
      try {
        const data = await blockedCustomerService.getBlockedCustomers(parseInt(id, 10));
        setBlockedCustomers(data);
      } catch (err) {
        console.error('Failed to fetch blocked customers', err);
      }
    }
  }, [id, formData.role]);

  // NGホテル一覧を取得（キャストのみ）
  const fetchNgHotels = useCallback(async () => {
    if (id && formData.role === 'CAST') {
      try {
        const data = await ngHotelService.getNgHotels(parseInt(id, 10));
        setNgHotels(data);
      } catch (err) {
        console.error('Failed to fetch ng hotels', err);
      }
    }
  }, [id, formData.role]);

  // カタカナをひらがなに変換（ホテルかな検索用）
  const toHiragana = (str: string) =>
    str.replace(/[\u30a1-\u30f6]/g, (m) => String.fromCharCode(m.charCodeAt(0) - 0x60));

  // ホテル名のかな検索フィルタ（新規オーダー画面と同じ挙動）
  const filterHotelOptions = (options: any[], { inputValue }: { inputValue: string }) => {
    const q = toHiragana(inputValue.toLowerCase());
    return options.filter((o) => {
      const name = (o.name || '').toLowerCase();
      const kana = (o.name_kana || '').toLowerCase();
      return name.includes(q) || kana.includes(q) || toHiragana(kana).includes(q);
    });
  };

  // マウント時にホテル一覧を取得（NGホテル選択の候補）
  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const res = await systemSettingsService.getHotels();
        setHotelOptions(Array.isArray(res) ? res : []);
      } catch (err) {
        console.error('ホテル一覧の取得に失敗しました', err);
        setHotelOptions([]);
      }
    };
    fetchHotels();
  }, []);

  const handleAddNgHotel = async () => {
    if (!id) return;
    if (!selectedNgHotel) {
      toast.error('ホテルを選択してください。');
      return;
    }
    try {
      await ngHotelService.addNgHotel(parseInt(id, 10), {
        hotel_id: selectedNgHotel.id,
        hotel_name: selectedNgHotel.name,
        notes: newNgHotelNotes,
      });
      toast.success('NGホテルを追加しました。');
      setSelectedNgHotel(null);
      setNewNgHotelNotes('');
      fetchNgHotels();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'NGホテルの追加に失敗しました。');
    }
  };

  const handleDeleteNgHotel = async (ngHotelId: number) => {
    if (!id) return;
    try {
      await ngHotelService.deleteNgHotel(parseInt(id, 10), ngHotelId);
      setNgHotels((prev) => prev.filter((h) => h.id !== ngHotelId));
    } catch (err) {
      console.error('Failed to delete ng hotel', err);
      toast.error('NGホテルの削除に失敗しました。');
    }
  };

  // キャスト個別の待機場所
  const fetchCastWaitingLocations = useCallback(async () => {
    if (id && formData.role === 'CAST') {
      try {
        const data = await castWaitingLocationService.getCastWaitingLocations(parseInt(id, 10));
        setCastWaitingLocations(data);
      } catch (err) {
        console.error('Failed to fetch cast waiting locations', err);
      }
    }
  }, [id, formData.role]);

  const handleAddCastWaiting = async () => {
    if (!id) return;
    if (!newWaitingName.trim() && !newWaitingAddress.trim()) {
      toast.error('名前または住所を入力してください。');
      return;
    }
    setSavingCastWaiting(true);
    try {
      // 座標はバックエンドが住所から補完する
      await castWaitingLocationService.addCastWaitingLocation(parseInt(id, 10), {
        name: newWaitingName,
        name_kana: newWaitingKana,
        address: newWaitingAddress,
      });
      toast.success('待機場所を追加しました。');
      setNewWaitingName('');
      setNewWaitingKana('');
      setNewWaitingAddress('');
      fetchCastWaitingLocations();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '待機場所の追加に失敗しました。');
    } finally {
      setSavingCastWaiting(false);
    }
  };

  const handleDeleteCastWaiting = async (locId: number) => {
    if (!id) return;
    try {
      await castWaitingLocationService.deleteCastWaitingLocation(parseInt(id, 10), locId);
      setCastWaitingLocations((prev) => prev.filter((w) => w.id !== locId));
    } catch (err) {
      console.error('Failed to delete cast waiting location', err);
      toast.error('待機場所の削除に失敗しました。');
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const userId = parseInt(id, 10);
        const userData = await userService.getUser(userId);
        setUser(userData); // ★取得したUserオブジェクトをそのままセット

        // 取得したデータでフォームの初期値を設定
        const customer = userData.customer;
        const cast = userData.cast;
        setFormData({
          profile_image: null,
          username: userData.username || '',
          password: '',
          current_password: '',
          phone_number: userData.phone_number || customer?.phone_number || '',
          email: userData.email || customer?.contact_email || '',
          line_account: userData.line_account || '',
          role: userData.role || '',
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          is_active: userData.is_active,
          // Customer data
          customer_name: customer?.customer_name || '',
          furigana: customer?.furigana || '',
          gender: customer?.gender || 'unspecified',
          birthday: customer?.birthday ? customer.birthday.split('T')[0] : '',
          postal_code: customer?.postal_code || '',
          address: customer?.address || '',
          contact_email: customer?.contact_email || customer?.email || '',
          payment_method: customer?.payment_method || '',
          company_name: customer?.company_name || '',
          notes: customer?.notes || '',
          blacklisted: customer?.blacklisted || false,
          blacklist_reason: customer?.blacklist_reason || '',
          ng_cast_ids: Array.isArray(customer?.ng_cast_ids) ? customer.ng_cast_ids : (customer?.ng_cast_ids ? customer.ng_cast_ids.split(',').filter(Boolean) : []),
          // Cast data
          options: cast?.options || [],
          nomination_fee: cast?.nomination_fee || '',
          join_date: cast?.join_date || '',
          age: cast?.age || '',
          height: cast?.height || '',
          bust: cast?.bust || '',
          cup: cast?.cup || '',
          waist: cast?.waist || '',
          hip: cast?.hip || '',
          spec: cast?.spec || '',
          special_nomination_fee: cast?.special_nomination_fee || '',
          // Driver data（車両情報）
          car_model: (userData as any).driver?.car_model || (userData as any).car_model || '',
          car_number: (userData as any).driver?.car_number || (userData as any).car_number || '',
          car_color: (userData as any).driver?.car_color || (userData as any).car_color || '',
        });

        if (userData.profile_image) {
          const imageUrl = userData.profile_image.startsWith('http') 
            ? userData.profile_image 
            : `${config.api.baseURL}${userData.profile_image}`;
          setPreviewImage(imageUrl);
        }
      } catch (error: any) {
        toast.error(error.message || 'ユーザー情報の取得に失敗しました');
        navigate('/users');
      } finally {
        setLoading(false);
      }
    };

    const fetchCasts = async () => {
      try {
        const response = await userService.getUsers(1, 0, { role: 'CAST' }); 
        setCasts(response.users);
        setFilteredCasts(response.users);
      } catch (error) {
        console.error('キャスト取得エラー:', error);
        toast.error('キャスト一覧の取得に失敗しました。');
      }
    };

    fetchUser();
    fetchCasts();
  }, [id, navigate]);

  useEffect(() => {
    if (user) {
      fetchBlockedCustomers();
      fetchNgHotels();
      fetchCastWaitingLocations();
    }
  }, [user, fetchBlockedCustomers, fetchNgHotels, fetchCastWaitingLocations]);

  useEffect(() => {
    if (!castSearchTerm) {
      setFilteredCasts(casts);
      return;
    }
    const lowercasedFilter = castSearchTerm.toLowerCase();
    const filtered = casts.filter(cast => {
      const lastName = cast.last_name === 'nan' ? '' : (cast.last_name || '');
      const firstName = cast.first_name || '';
      const fullName = `${lastName} ${firstName}`.toLowerCase();
      return fullName.includes(lowercasedFilter);
    });
    setFilteredCasts(filtered);
  }, [castSearchTerm, casts]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, profile_image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    if (name) {
      const finalValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
      setFormData(prev => ({ ...prev, [name]: finalValue }));
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string | string[]>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.username.trim()) newErrors.username = 'ユーザー名は必須です';
    if (!formData.email.trim()) {
      newErrors.email = 'メールアドレスは必須です';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleToggleStatus = async () => {
    if (!user) return;
    const actionText = formData.is_active ? '無効化' : '有効化';
    if (window.confirm(`このユーザーを${actionText}しますか？`)) {
      try {
        const updatedUser = await userService.toggleUserStatus(user.id);
        setUser(updatedUser);
        setFormData(prev => ({ ...prev, is_active: updatedUser.is_active }));
        toast.success(`ユーザーのステータスを${actionText}しました。`);
      } catch (error: any) {
        toast.error(error.message || 'ステータスの変更に失敗しました。');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm() || !id || !user) {
      toast.error('入力内容に誤りがあります。');
      return;
    }
    setSavingLoading(true);

    // 送信用のペイロードを UserUpdatePayload 型で構築
    const payload: UserUpdatePayload = {
      // User のプロパティ
      username: formData.username,
      email: formData.email,
      phone_number: formData.phone_number,
      first_name: formData.first_name,
      last_name: formData.last_name,
      role: formData.role,
      line_account: formData.line_account,
      is_active: formData.is_active,
    };

    // パスワードが入力されていれば追加
    if (formData.password) {
      payload.password = formData.password;
    }

    // 権限が CUSTOMER の場合、customer 情報を追加
    if (formData.role === 'CUSTOMER') {
      payload.customer = {
        customer_name: formData.customer_name,
        furigana: formData.furigana,
        gender: formData.gender,
        birthday: formData.birthday || undefined,
        postal_code: formData.postal_code,
        address: formData.address,
        contact_email: formData.contact_email,
        payment_method: formData.payment_method,
        company_name: formData.company_name,
        notes: formData.notes,
        blacklisted: formData.blacklisted,
        blacklist_reason: formData.blacklist_reason,
        ng_cast_ids: formData.ng_cast_ids.join(','), // string[] から string へ変換
        phone_number: formData.phone_number,
      };
    }

    // 権限が CAST の場合、cast 情報を追加
    if (formData.role === 'CAST') {
      payload.cast = {
        options: formData.options,
        nomination_fee: formData.nomination_fee,
        join_date: formData.join_date,
        age: formData.age,
        height: formData.height,
        bust: formData.bust,
        cup: formData.cup,
        waist: formData.waist,
        hip: formData.hip,
        spec: formData.spec,
        special_nomination_fee: formData.special_nomination_fee,
      };
    }

    // 権限が DRIVER の場合、車両情報(driver)を追加
    if (String(formData.role ?? '').trim().toLowerCase() === 'driver') {
      (payload as any).driver = {
        car_model: formData.car_model,
        car_number: formData.car_number,
        car_color: formData.car_color,
      };
    }

    try {
      const userId = parseInt(id, 10);
      await userService.updateUser(userId, payload);
      toast.success('ユーザー情報が正常に更新されました');
      navigate('/users');
    } catch (error: any) {
      console.error('ユーザー更新エラー:', error);
      toast.error(error.response?.data?.message || 'ユーザー情報の更新に失敗しました');
    } finally {
      setSavingLoading(false);
    }
  };

  const handleNgCastChange = (castId: string) => {
    setFormData(prev => {
      const currentNgCasts = prev.ng_cast_ids || [];
      const newNgCasts = currentNgCasts.includes(castId)
        ? currentNgCasts.filter(id => id !== castId)
        : [...currentNgCasts, castId];
      return { ...prev, ng_cast_ids: newNgCasts };
    });
  };

  const handleAddBlockedCustomer = async () => {
    if (!id || !newBlockedPhone) {
      toast.error('電話番号を入力してください。');
      return;
    }
    try {
      await blockedCustomerService.addBlockedCustomer(parseInt(id, 10), {
        phone_number: newBlockedPhone,
        notes: newBlockedNotes,
      });
      toast.success('NG電話番号を追加しました。');
      setNewBlockedPhone('');
      setNewBlockedNotes('');
      fetchBlockedCustomers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'NG電話番号の追加に失敗しました。');
    }
  };

  const handleDeleteBlockedCustomer = async (blockedCustomerId: number) => {
    if (!id) return;
    try {
      await blockedCustomerService.deleteBlockedCustomer(parseInt(id, 10), blockedCustomerId);
      setBlockedCustomers(blockedCustomers.filter(bc => bc.id !== blockedCustomerId));
    } catch (err) {
      console.error('Failed to delete blocked customer', err);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <PageHeader title="ユーザー編集" subtitle="ユーザー情報を更新します" />
        <Paper sx={{ p: 3, mt: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Avatar src={previewImage || undefined} sx={{ width: 120, height: 120, mb: 2 }} />
                  <input accept="image/*" style={{ display: 'none' }} id="profile-image" type="file" onChange={handleImageChange} />
                  <label htmlFor="profile-image">
                    <IconButton color="primary" component="span"><PhotoCamera /></IconButton>
                  </label>
                  {previewImage && (
                    <IconButton size="small" onClick={() => { setPreviewImage(null); setFormData(prev => ({ ...prev, profile_image: null })); }}>
                      <CloseIcon />
                    </IconButton>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom>基本情報</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}><TextField fullWidth label="姓" name="last_name" value={formData.last_name} onChange={handleInputChange} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="名" name="first_name" value={formData.first_name} onChange={handleInputChange} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="ユーザー名" name="username" value={formData.username} onChange={handleInputChange} error={!!errors.username} helperText={errors.username} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth type="password" label="現在のパスワード" name="current_password" value={formData.current_password} onChange={handleInputChange} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth type="password" label="新しいパスワード（変更する場合のみ）" name="password" value={formData.password} onChange={handleInputChange} /></Grid>
                  <Grid item xs={12}><Box sx={{ display: 'flex', justifyContent: 'flex-end' }}><Button size="small" color="secondary" component={Link} to="/password-reset">パスワードを忘れた場合</Button></Box></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="電話番号" name="phone_number" value={formData.phone_number} onChange={handleInputChange} error={!!errors.phone_number} helperText={errors.phone_number} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="メールアドレス" name="email" type="email" value={formData.email} onChange={handleInputChange} error={!!errors.email} helperText={errors.email} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="LINEアカウント" name="line_account" value={formData.line_account} onChange={handleInputChange} /></Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors.role}>
                      <InputLabel id="role-label">権限</InputLabel>
                      <Select labelId="role-label" name="role" value={formData.role} onChange={handleSelectChange as any} label="権限">
                        <MenuItem value="ADMIN">管理者</MenuItem>
                        <MenuItem value="MANAGER">マネージャー</MenuItem>
                        <MenuItem value="CUSTOMER">顧客</MenuItem>
                        <MenuItem value="CAST">キャスト</MenuItem>
                        <MenuItem value="DRIVER">ドライバー</MenuItem>
                        <MenuItem value="STAFF">内勤スタッフ</MenuItem>
                      </Select>
                      {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              {formData.role === 'CUSTOMER' && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>顧客情報</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}><TextField fullWidth label="顧客名" name="customer_name" value={formData.customer_name} onChange={handleInputChange} /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="フリガナ" name="furigana" value={formData.furigana} onChange={handleInputChange} /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="会社名" name="company_name" value={formData.company_name} onChange={handleInputChange} /></Grid>
                    <Grid item xs={12} md={6}>
                       <FormControl component="fieldset">
                        <FormLabel>性別</FormLabel>
                        <RadioGroup row name="gender" value={formData.gender} onChange={handleInputChange}>
                          <FormControlLabel value="male" control={<Radio />} label="男" />
                          <FormControlLabel value="female" control={<Radio />} label="女" />
                          <FormControlLabel value="unspecified" control={<Radio />} label="未設定" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                     <Grid item xs={12} md={6}><TextField fullWidth type="date" label="誕生日" name="birthday" value={formData.birthday} onChange={handleInputChange} InputLabelProps={{ shrink: true }} /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="顧客連絡先メール" name="contact_email" value={formData.contact_email} onChange={handleInputChange} /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="郵便番号" name="postal_code" value={formData.postal_code} onChange={handleInputChange} /></Grid>
                     <Grid item xs={12} md={6}><TextField fullWidth label="支払い方法" name="payment_method" value={formData.payment_method} onChange={handleInputChange} /></Grid>
                    <Grid item xs={12}><TextField fullWidth label="住所" name="address" value={formData.address} onChange={handleInputChange} /></Grid>
                     <Grid item xs={12}>
                      <FormControlLabel
                        control={<Switch checked={formData.blacklisted} onChange={handleInputChange} name="blacklisted" />}
                        label="ブラックリスト"
                      />
                    </Grid>
                    {formData.blacklisted && (
                      <Grid item xs={12}><TextField fullWidth multiline rows={2} label="ブラックリスト理由" name="blacklist_reason" value={formData.blacklist_reason} onChange={handleInputChange} /></Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom>NGキャスト（複数選択可）</Typography>
                      <TextField fullWidth label="キャスト名で検索" placeholder="名前を入力して絞り込み" value={castSearchTerm} sx={{ mb: 2 }} onChange={(e) => setCastSearchTerm(e.target.value)} />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {filteredCasts.map((cast) => (
                          <Chip
                            key={cast.id}
                            label={`${cast.last_name === 'nan' ? '' : (cast.last_name || '')} ${cast.first_name || ''}`}
                            onClick={() => handleNgCastChange(cast.id.toString())}
                            color={formData.ng_cast_ids.includes(cast.id.toString()) ? 'primary' : 'default'}
                            variant={formData.ng_cast_ids.includes(cast.id.toString()) ? 'filled' : 'outlined'}
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12}><TextField fullWidth multiline rows={3} label="備考" name="notes" value={formData.notes} onChange={handleInputChange} /></Grid>
                  </Grid>
                </Grid>
              )}

              {formData.role === 'CAST' && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>キャスト情報</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}><TextField fullWidth type="date" label="入店日" name="join_date" value={formData.join_date} onChange={handleInputChange} InputLabelProps={{ shrink: true }} /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth type="number" label="年齢" name="age" value={formData.age} onChange={handleInputChange} /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth type="number" label="身長" name="height" value={formData.height} onChange={handleInputChange} /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth type="number" label="バスト" name="bust" value={formData.bust} onChange={handleInputChange} /></Grid>
                     <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>カップ</InputLabel>
                        <Select name="cup" value={formData.cup} onChange={handleSelectChange as any} label="カップ">
                          <MenuItem value="A">A</MenuItem><MenuItem value="B">B</MenuItem><MenuItem value="C">C</MenuItem><MenuItem value="D">D</MenuItem>
                          <MenuItem value="E">E</MenuItem><MenuItem value="F">F</MenuItem><MenuItem value="G">G</MenuItem><MenuItem value="H">H</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth type="number" label="ウエスト" name="waist" value={formData.waist} onChange={handleInputChange} /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth type="number" label="ヒップ" name="hip" value={formData.hip} onChange={handleInputChange} /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth type="number" label="指名料" name="nomination_fee" value={formData.nomination_fee} onChange={handleInputChange} error={!!errors.nomination_fee} helperText={errors.nomination_fee} /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth type="number" label="本指名料" name="special_nomination_fee" value={formData.special_nomination_fee} onChange={handleInputChange} error={!!errors.special_nomination_fee} helperText={errors.special_nomination_fee} /></Grid>
                    <Grid item xs={12}><TextField fullWidth multiline rows={3} label="特徴・備考" name="spec" value={formData.spec} onChange={handleInputChange} /></Grid>
                  </Grid>
                </Grid>
              )}

              {String(formData.role ?? '').trim().toLowerCase() === 'driver' && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>車両情報</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="車種" name="car_model" value={formData.car_model} onChange={handleInputChange} placeholder="例: トヨタ プリウス" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="ナンバー" name="car_number" value={formData.car_number} onChange={handleInputChange} placeholder="例: 青森 300 あ 12-34" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="色" name="car_color" value={formData.car_color} onChange={handleInputChange} placeholder="例: 白" />
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {formData.role === 'CAST' && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>NG電話番号管理</Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField label="NG電話番号" value={newBlockedPhone} onChange={(e) => setNewBlockedPhone(e.target.value)} variant="outlined" size="small" sx={{ flexGrow: 1 }} />
                    <TextField label="理由" value={newBlockedNotes} onChange={(e) => setNewBlockedNotes(e.target.value)} variant="outlined" size="small" sx={{ flexGrow: 2 }} />
                    <Button onClick={handleAddBlockedCustomer} variant="contained">追加</Button>
                  </Box>
                  <List>
                    {blockedCustomers.map((bc) => (
                      <ListItem key={bc.id} divider>
                        <ListItemText primary={bc.phone_number} secondary={bc.notes || '理由なし'} />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteBlockedCustomer(bc.id)}><DeleteIcon /></IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              )}

              {formData.role === 'CAST' && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>NGホテル管理</Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <Autocomplete
                      sx={{ flexGrow: 1 }}
                      size="small"
                      options={hotelOptions}
                      value={selectedNgHotel}
                      onChange={(_, val) => setSelectedNgHotel(val)}
                      getOptionLabel={(o) => `${o.name}${o.name_kana ? `（${o.name_kana}）` : ''}`}
                      filterOptions={filterHotelOptions}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      renderInput={(params) => (
                        <TextField {...params} label="NGホテル（かな検索対応）" variant="outlined" size="small" />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.name}{option.name_kana ? `（${option.name_kana}）` : ''}
                        </li>
                      )}
                    />
                    <TextField label="理由" value={newNgHotelNotes} onChange={(e) => setNewNgHotelNotes(e.target.value)} variant="outlined" size="small" sx={{ flexGrow: 1 }} />
                    <Button onClick={handleAddNgHotel} variant="contained">追加</Button>
                  </Box>
                  <List>
                    {ngHotels.map((h) => (
                      <ListItem key={h.id} divider>
                        <ListItemText primary={h.hotel_name || `ホテルID: ${h.hotel_id}`} secondary={h.notes || '理由なし'} />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteNgHotel(h.id)}><DeleteIcon /></IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              )}

              {formData.role === 'CAST' && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>待機場所（迎え場所）</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    このキャスト固有の待機場所を登録します（複数可）。住所から座標を自動取得し、オーダー作成の迎え場所候補に表示されます。
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField label="場所名" value={newWaitingName} onChange={(e) => setNewWaitingName(e.target.value)} variant="outlined" size="small" />
                    <TextField label="ふりがな" value={newWaitingKana} onChange={(e) => setNewWaitingKana(e.target.value)} variant="outlined" size="small" />
                    <TextField label="住所" value={newWaitingAddress} onChange={(e) => setNewWaitingAddress(e.target.value)} variant="outlined" size="small" sx={{ flexGrow: 1, minWidth: 220 }} />
                    <Button onClick={handleAddCastWaiting} variant="contained" disabled={savingCastWaiting}>
                      {savingCastWaiting ? '保存中...' : '追加'}
                    </Button>
                  </Box>
                  <List>
                    {castWaitingLocations.map((w) => (
                      <ListItem key={w.id} divider>
                        <ListItemText
                          primary={w.name || w.address || `待機場所${w.id}`}
                          secondary={
                            `${w.address || ''}` +
                            (w.latitude != null && w.longitude != null
                              ? `（${Number(w.latitude).toFixed(5)}, ${Number(w.longitude).toFixed(5)}）`
                              : '（座標なし）')
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteCastWaiting(w.id)}><DeleteIcon /></IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, alignItems: 'center' }}>
                  <Button variant="outlined" color="secondary" onClick={() => navigate('/users')} sx={{ mr: 2 }} disabled={savingLoading}>キャンセル</Button>
                  {user && (
                    <Button variant="contained" color={formData.is_active ? 'warning' : 'success'} onClick={handleToggleStatus} sx={{ mr: 2 }} disabled={savingLoading}>
                      {formData.is_active ? '無効にする' : '有効にする'}
                    </Button>
                  )}
                  <Button type="submit" variant="contained" color="primary" disabled={savingLoading || !user}>
                    {savingLoading ? <CircularProgress size={24} /> : '更新'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </DashboardLayout>
  );
};

export default EditUser;
